import './style.css';
import * as api from './api-client.js';
import { onQueueChange, queueLength, failedLength, flush as flushQueue } from './offline/queue.js';
import { navigate, parseHash, el, esc } from './ui/util.js';
import { bindThemeToggle } from './ui/theme.js';
import { DEFAULT_PATH, matchRoute, redirectFor } from './ui/routes.js';
import { openSyncPanel } from './ui/sync-panel.js';
import { openChangePasswordDialog } from './ui/change-password.js';

import { renderLogin } from './views/login.js';
import { renderHouseholdSearch, renderHouseholdDetail } from './views/household-search.js';
import { renderPlotCreate } from './views/plot-create.js';
import { renderPlotBoundary } from './views/plot-boundary.js';
import { renderMiddlemanList, renderMiddlemanDetail } from './views/middlemen.js';
import { renderMiddlemanImport } from './views/middleman-import.js';
import { renderSubmissionList, renderSubmissionNew } from './views/submission-list.js';
import { renderSubmissionForm } from './views/submission-form.js';
import { renderFormBuilder } from './views/form-builder.js';
import { renderAuditRules } from './views/audit-rules.js';
import { renderUsers } from './views/users.js';
import { renderInstances } from './views/instances.js';
import { renderReviewQueue } from './views/review-queue.js';
import { renderMasterDataChanges } from './views/master-data-changes.js';

const ROLE_LABEL = {
  enumerator: 'Điều tra viên',
  supervisor: 'Điều phối viên',
  instance_admin: 'Quản trị đơn vị',
  admin: 'Quản trị hệ thống',
};

const TRONG_DON_VI = ['enumerator', 'supervisor', 'instance_admin', 'admin'];

const NAV_ITEMS = [
  // Doc/11a: người duyệt làm việc từ hàng chờ. Quản trị hệ thống không thuộc đơn vị nào nên
  // không duyệt đề xuất (ma trận quyền Doc/11a) — không có mục này.
  { path: '/review', icon: 'inbox', label: 'Hàng chờ duyệt', roles: ['supervisor', 'instance_admin'] },
  { path: '/households', icon: 'groups', label: 'Hộ & Vườn', roles: TRONG_DON_VI },
  // Điều tra viên vẫn thấy mục này: xem hồ sơ MM là việc của họ, chỉ thêm/sửa là không.
  { path: '/middlemen', icon: 'storefront', label: 'Đại lý / MM', roles: TRONG_DON_VI },
  { path: '/submissions', icon: 'fact_check', label: 'Phiếu điều tra', roles: TRONG_DON_VI },
  // Doc/11a: đối soát sau khi duyệt, truy trách nhiệm. Điều tra viên xem nhật ký trong từng hồ sơ.
  { path: '/change-log', icon: 'manage_history', label: 'Nhật ký thay đổi', roles: ['supervisor', 'instance_admin', 'admin'] },
  { path: '/forms', icon: 'account_tree', label: 'Bộ câu hỏi', roles: ['admin'] },
  { path: '/audit-rules', icon: 'rule', label: 'Mục đối chiếu', roles: ['admin'] },
  // Quản trị đơn vị quản lý nhân sự trong đơn vị mình — cùng màn hình, phạm vi hẹp
  // hơn, và việc thu hẹp đó do máy chủ áp chứ không do việc ẩn nút ở đây (R7).
  { path: '/users', icon: 'manage_accounts', label: 'Tài khoản', roles: ['instance_admin', 'admin'] },
  { path: '/instances', icon: 'domain', label: 'Đơn vị điều tra', roles: ['admin'] },
];

const ROUTES = [
  { re: /^\/login$/, view: renderLogin, public: true },
  { re: /^\/households$/, view: renderHouseholdSearch },
  { re: /^\/households\/([^/]+)$/, view: renderHouseholdDetail },
  { re: /^\/plots\/new$/, view: renderPlotCreate },
  { re: /^\/plots\/([^/]+)\/boundary$/, view: renderPlotBoundary },
  { re: /^\/middlemen$/, view: renderMiddlemanList },
  // Trước `/middlemen/:id` — nếu không "import" bị khớp như một id.
  { re: /^\/middlemen\/import$/, view: renderMiddlemanImport },
  { re: /^\/middlemen\/([^/]+)$/, view: renderMiddlemanDetail },
  { re: /^\/submissions$/, view: renderSubmissionList },
  { re: /^\/submissions\/new$/, view: renderSubmissionNew },
  { re: /^\/submissions\/([^/]+)$/, view: renderSubmissionForm },
  { re: /^\/forms$/, view: renderFormBuilder },
  { re: /^\/audit-rules$/, view: renderAuditRules },
  { re: /^\/users$/, view: renderUsers },
  { re: /^\/instances$/, view: renderInstances },
  { re: /^\/review$/, view: renderReviewQueue },
  { re: /^\/change-log$/, view: renderMasterDataChanges },
];

const app = document.getElementById('app');
let currentCleanup = null;

function renderShell() {
  const user = api.getCurrentUser();
  app.innerHTML = '';
  const header = el('header', 'app-header');
  header.innerHTML = `
    <div class="app-brand"><span class="ms">forest</span><span class="brand-text">Farmer Survey</span></div>
    <nav class="app-nav"></nav>
    <div class="sync-indicator" id="syncIndicator"><span class="ms">cloud_done</span><span>Đã đồng bộ</span></div>
    <div class="app-user">
      ${user ? `<span class="user-name">${esc(user.fullName)}</span><span class="role-pill">${esc(ROLE_LABEL[user.role] || user.role)}</span>` : ''}
      ${user?.instanceName ? `<span class="role-pill instance-pill" title="Đơn vị điều tra">${esc(user.instanceName)}</span>` : ''}
      ${user ? `<button class="icon-btn sm" id="passwordBtn" title="Đổi mật khẩu" aria-label="Đổi mật khẩu"><span class="ms ms-sm">key</span></button>` : ''}
      <button class="icon-btn sm" id="themeBtn"></button>
      ${user ? `<button class="btn btn-text sm" id="logoutBtn" title="Đăng xuất" aria-label="Đăng xuất"><span class="ms ms-sm">logout</span><span class="btn-label">Đăng xuất</span></button>` : ''}
    </div>`;
  const nav = header.querySelector('.app-nav');
  NAV_ITEMS.filter((n) => !user || n.roles.includes(user.role)).forEach((n) => {
    const a = document.createElement('a');
    a.href = `#${n.path}`;
    a.innerHTML = `<span class="ms ms-sm">${n.icon}</span>${esc(n.label)}`;
    nav.appendChild(a);
  });
  bindThemeToggle(header.querySelector('#themeBtn'));
  header.querySelector('#passwordBtn')?.addEventListener('click', openChangePasswordDialog);
  header.querySelector('#logoutBtn')?.addEventListener('click', async () => {
    // `api.logout()` xoá phiên cục bộ trong khối `finally` rồi mới để lỗi mạng nổi
    // lên. Không bắt ở đây thì mất mạng hoặc server 500 sẽ chặn luôn dòng
    // `navigate` phía dưới: phiên đã xoá mà màn hình đứng yên — nút Đăng xuất
    // trông như hỏng. Rời khỏi trang là việc đúng trong mọi trường hợp.
    try {
      await api.logout();
    } catch (err) {
      console.warn('[main] gọi /auth/logout thất bại, vẫn đăng xuất tại máy', err);
    }
    navigate('#/login');
  });
  // Chỉ báo đồng bộ mở được bảng chi tiết khi có thay đổi bị máy chủ từ chối.
  // Gắn ở đây vì `renderShell()` dựng lại thanh tiêu đề sau mỗi lần điều hướng —
  // gắn một lần lúc khởi động thì lần chuyển trang đầu tiên là mất listener.
  const indicator = header.querySelector('#syncIndicator');
  const openIfFailed = async () => {
    if ((await failedLength()) > 0) openSyncPanel();
  };
  indicator.addEventListener('click', openIfFailed);
  indicator.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openIfFailed();
    }
  });

  const main = el('main', 'view');
  main.id = 'view';
  app.append(header, main);
  updateSyncIndicator();
}

async function updateSyncIndicator() {
  const el_ = document.getElementById('syncIndicator');
  if (!el_) return;
  const [n, hong] = await Promise.all([queueLength(), failedLength()]);
  const online = navigator.onLine;

  // Thứ tự ưu tiên: thay đổi bị máy chủ TỪ CHỐI xếp trên "đang chờ gửi" và trên
  // cả "mất mạng". Mất mạng rồi sẽ có lại; op hỏng thì không tự khỏi, phải có
  // người xử lý — nên nó là thứ duy nhất ở đây đáng đòi người dùng bấm vào.
  const [tone, icon, label] =
    hong > 0
      ? ['error', 'error', `${hong} thay đổi chưa gửi được`]
      : !online
        ? ['warn', 'cloud_off', 'Mất mạng — dữ liệu lưu tại máy']
        : n > 0
          ? ['warn', 'sync', `Đang đồng bộ (${n} thay đổi)`]
          : ['', 'cloud_done', 'Đã đồng bộ'];
  el_.className = `sync-indicator ${tone}`.trim();
  el_.innerHTML = `<span class="ms">${icon}</span><span>${label}</span>`;

  // Chỉ bấm được khi có gì để xem — nút bấm vào không ra gì còn tệ hơn không có nút.
  el_.classList.toggle('clickable', hong > 0);
  el_.toggleAttribute('role', hong > 0);
  if (hong > 0) {
    el_.setAttribute('role', 'button');
    el_.tabIndex = 0;
    el_.title = `${label} — bấm để xem các thay đổi máy chủ đã từ chối`;
  } else {
    el_.removeAttribute('tabindex');
    // Màn hẹp chỉ hiện biểu tượng (layout.css) — chữ trạng thái vẫn đọc được khi trỏ vào.
    el_.title = label;
  }
}

onQueueChange(updateSyncIndicator);
window.addEventListener('online', () => {
  updateSyncIndicator();
  flushQueue();
});
window.addEventListener('offline', updateSyncIndicator);
setInterval(updateSyncIndicator, 15_000);

function highlightNav(path) {
  document.querySelectorAll('.app-nav a').forEach((a) => {
    a.classList.toggle('active', path.startsWith(a.getAttribute('href').slice(1)));
  });
}

// Mỗi lần điều hướng nhận một token. Router là hàm async: nếu người dùng đổi
// trang giữa chừng, lần chạy cũ phải dừng lại thay vì gắn tiếp DOM của nó — nếu
// không, các view gắn phần tử ở `document.body` (ngăn cài đặt, trình đơn xuất)
// sẽ để lại bản mồ côi không có ai nghe sự kiện.
let navToken = 0;

async function router() {
  const token = ++navToken;
  const { path, params } = parseHash();
  const loggedIn = api.isLoggedIn();

  // Gồm cả trường hợp URL trần (`/`): trước đây nó rơi thẳng vào "Không tìm thấy
  // trang" vì bảng route không có mục nào cho nó.
  const redirect = redirectFor(path, loggedIn);
  if (redirect) {
    navigate(`#${redirect}`);
    return;
  }

  renderShell();
  highlightNav(path);
  const view = document.getElementById('view');
  const hit = matchRoute(path, ROUTES);

  if (typeof currentCleanup === 'function') {
    try {
      currentCleanup();
    } catch (err) {
      console.error(err);
    }
    currentCleanup = null;
  }

  if (!hit) {
    view.innerHTML = `<div class="empty-state"><span class="ms ms-xl">search_off</span>
      <p>Không tìm thấy trang.</p>
      <a class="btn btn-tonal" href="#${DEFAULT_PATH}">Về trang chính</a></div>`;
    return;
  }
  const routeParams = { ...params, id: hit.id };
  try {
    const cleanup = await hit.route.view(view, routeParams);
    if (typeof cleanup !== 'function') return;
    // Lần điều hướng này đã bị thay thế trong lúc view đang tải → dọn ngay,
    // đừng để cleanup của nó nằm lại chờ lần chuyển trang sau.
    if (token !== navToken) cleanup();
    else currentCleanup = cleanup;
  } catch (err) {
    console.error(err);
    view.innerHTML = `<div class="empty-state"><span class="ms ms-xl">error</span><p>${esc(err.message || 'Đã xảy ra lỗi.')}</p></div>`;
    if (err?.status === 401) navigate('#/login');
  }
}

/**
 * Làm tươi hồ sơ người dùng một lần lúc khởi động.
 *
 * `user` trong localStorage được chụp lúc đăng nhập và không bao giờ đổi. Quản trị
 * đổi vai trò hoặc địa bàn xong, người dùng vẫn thấy menu cũ cho tới lần đăng nhập
 * kế tiếp — bấm vào mục không còn quyền thì nhận 403 mà không hiểu vì sao.
 *
 * Chỉ vẽ lại khi thực sự khác: gọi `router()` vô điều kiện sẽ dựng lại view ngay
 * sau khi nó vừa dựng xong, làm mất trạng thái cuộn và mọi thứ người dùng vừa gõ.
 */
async function refreshSessionUser() {
  if (!api.isLoggedIn()) return;
  const truoc = api.getCurrentUser();
  try {
    const sau = await api.fetchMe();
    if (truoc?.role !== sau.role || truoc?.regionId !== sau.regionId) {
      router();
    }
  } catch (err) {
    // Mất mạng thì dùng tiếp bản đang lưu — không có lý do gì đá người dùng ra chỉ
    // vì không xác nhận lại được hồ sơ. Phiên hết hạn thật thì request đầu tiên của
    // view sẽ tự xử lý (401 -> refresh -> đăng nhập lại).
    console.warn('[main] không làm tươi được hồ sơ người dùng', err);
  }
}

window.addEventListener('hashchange', router);
// Đúng một lần khởi động: trước đây cả 'DOMContentLoaded' lẫn nhánh readyState
// đều gọi router, nên mỗi lần tải trang view chạy hai lượt.
function boot() {
  router();
  refreshSessionUser();
}
if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
